import { useCallback, useEffect, useMemo, useRef } from 'react';
import { equals } from 'rambda';

export type SseMessageHandler<TRes> = (data: TRes, unsubscribe: () => void) => void;

export type SseSubscribeOptions<TRes> = {
    /** Deep-compared, so an unmemoised array argument does not resubscribe. */
    deps: unknown[];
    subscribe: (onData: (data: TRes) => void, onClose: () => void) => () => void;
    onMessage: SseMessageHandler<TRes>;
};

export function useSseSubscribe<TRes>({ deps: depsRaw, subscribe: openStream, onMessage }: SseSubscribeOptions<TRes>) {
    const onMessageRef = useRef(onMessage);
    const openStreamRef = useRef(openStream);

    useEffect(() => {
        onMessageRef.current = onMessage;
        openStreamRef.current = openStream;
    });

    const depsRef = useRef<unknown[] | undefined>(undefined);
    const deps = useMemo(() => {
        if (!equals(depsRef.current, depsRaw)) {
            depsRef.current = depsRaw;
        }

        return depsRef.current;
    }, [depsRaw]);

    const closeStreamRef = useRef<(() => void) | null>(null);

    const unsubscribe = useCallback(() => {
        closeStreamRef.current?.();
        closeStreamRef.current = null;
    }, []);

    const subscribe = useCallback(() => {
        if (closeStreamRef.current) {
            return unsubscribe;
        }

        closeStreamRef.current = openStreamRef.current(
            (data) => onMessageRef.current(data, unsubscribe),
            // The stream can end on its own: terminal snapshot, exhausted retries, or deadline.
            // Forget the dead handle so a later subscribe() can reopen.
            () => {
                closeStreamRef.current = null;
            },
        );

        return unsubscribe;
        // deps is an identity trigger, not a value: it is the only thing that may open a
        // different stream, so subscribe must get a new identity when it changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deps, unsubscribe]);

    useEffect(() => unsubscribe, [unsubscribe]);

    return subscribe;
}
